import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import { FaTimes, FaSearch, FaChevronDown, FaChevronUp, FaSpinner } from 'react-icons/fa';
import TemplateRenderer from '@/components/ActionItems/TemplateRenderer';
import '@/styles/CreateActionItemModal.scss';
import * as Sentry from '@sentry/react';

/**
 * CreateActionItemModal — production-ready modal for creating action items.
 *
 * Architecture
 * ────────────
 *  ┌─────────────────────────────────────────┐
 *  │ Fixed header  — title + close button    │
 *  ├─────────────────────────────────────────┤
 *  │ Scrollable body                         │
 *  │  ┌ Basics: type, property, title, prio ┐│
 *  │  ├ Template Fields (TemplateRenderer)   ┤│
 *  │  └ Assignment accordion (collapsed)     ┘│
 *  ├─────────────────────────────────────────┤
 *  │ Fixed footer  — Cancel / Save           │
 *  └─────────────────────────────────────────┘
 *
 * The TemplateRenderer component handles sections, rows, multi-column layouts,
 * data-linked fields, signatures, property selectors, and all other template
 * schema features — so this modal no longer reimplements field rendering.
 */
const CreateActionItemModal = ({
    onClose,
    onCreated,
    boardCreationTypes,
    loadingCreationTypes,
    selectedCreationType: initialSelectedType,
    properties,
    allUsers,
    departments,
    functions,
    itCategories,
    getUserDisplayName,
    selectedBoardId,
}) => {
    const { apiFetch, user } = useAuth();

    // ── Local state ──
    const [selectedCreationType, setSelectedCreationType] = useState(initialSelectedType);
    const [activeTemplate, setActiveTemplate] = useState(null);
    const [loadingTemplate, setLoadingTemplate] = useState(false);
    const [saving, setSaving] = useState(false);

    // Core form fields (outside the template)
    const [propertyID, setPropertyID] = useState(
        properties.length === 1 ? String(properties[0].propertyID || properties[0].PropertyID || '') : ''
    );
    const [title, setTitle] = useState('');
    const [priority, setPriority] = useState('Medium');
    const [comments, setComments] = useState('');
    const [assignedToUserID, setAssignedToUserID] = useState('');
    const [assignedToDepartmentID, setAssignedToDepartmentID] = useState(
        initialSelectedType?.defaultDepartmentId ? String(initialSelectedType.defaultDepartmentId) : ''
    );
    const [assignedToFunctionID, setAssignedToFunctionID] = useState('');
    const [itCategoryId, setItCategoryId] = useState('');
    const [observers, setObservers] = useState([]);

    // Template form data (collected by TemplateRenderer's onSubmit)
    const templateFormRef = useRef({});

    // Accordion state for Assignment section
    const [assignmentExpanded, setAssignmentExpanded] = useState(false);

    // Observer search
    const [observerQuery, setObserverQuery] = useState('');
    const [showObserverDropdown, setShowObserverDropdown] = useState(false);
    const observerRef = useRef(null);

    // Load template on mount
    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!selectedCreationType) {
                if (!cancelled) setActiveTemplate(null);
                return;
            }

            const templateKey = selectedCreationType.templateKey;

            const fallback = {
                templateKey: selectedCreationType.contextKey || templateKey || 'Manual',
                displayName: selectedCreationType.displayName || templateKey || 'Action Item',
                defaultPriority: 'Low',
                schema: JSON.stringify({
                    fields: [{ name: 'description', label: 'Description', type: 'textarea', required: false }]
                })
            };

            if (!templateKey) {
                if (!cancelled) setActiveTemplate(fallback);
                return;
            }

            if (!cancelled) setLoadingTemplate(true);
            try {
                const res = await apiFetch(`/api/Templates/${templateKey}`);
                const data = await res.json();
                if (!cancelled) {
                    if (res.ok && data.success && data.data) {
                        setActiveTemplate(data.data);
                        if (data.data.defaultPriority) setPriority(data.data.defaultPriority);
                    } else {
                        setActiveTemplate(fallback);
                    }
                }
            } catch (e) {
                Sentry.captureException(e);
                if (!cancelled) setActiveTemplate(fallback);
            } finally {
                if (!cancelled) setLoadingTemplate(false);
            }
        };

        run();
        return () => { cancelled = true; };
    }, [selectedCreationType, apiFetch]);

    // Close observer dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (observerRef.current && !observerRef.current.contains(event.target)) {
                setShowObserverDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Escape key closes modal
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    const handleCreationTypeChange = (typeId) => {
        const type = boardCreationTypes.find(t => t.assignmentId === typeId);
        if (type) {
            setSelectedCreationType(type);
            templateFormRef.current = {};

            // Auto-populate default department from the selected type
            // Only apply if the user hasn't manually chosen a department yet
            if (type.defaultDepartmentId) {
                setAssignedToDepartmentID(String(type.defaultDepartmentId));
            }
        }
    };

    /**
     * Called by the TemplateRenderer when the user fills in the template form.
     * We store the data in a ref so it doesn't trigger re-renders while typing.
     */
    const handleTemplateDataChange = useCallback((formData) => {
        templateFormRef.current = formData;
    }, []);

    /**
     * Primary save handler. Validates basics → collects template data → posts to API.
     */
    const handleSave = async () => {
        // ── Validate basics ──
        if (!propertyID) {
            toast.error('Property is required');
            return;
        }
        if (!title.trim()) {
            toast.error('Title is required');
            return;
        }

        setSaving(true);
        try {
            // ── Build dataJson from template form data ──
            const dataJson = { ...templateFormRef.current };

            const templateKey = selectedCreationType?.templateKey || activeTemplate?.templateKey || null;

            const payload = {
                propertyID: Number(propertyID),
                templateKey,
                title: title.trim(),
                description: dataJson.description || '',
                priority,
                comments: comments || '',
                tags: dataJson.tags || '',
                dueDate: dataJson.dueDate ? new Date(dataJson.dueDate).toISOString() : null,
                assignedToUserID: assignedToUserID ? Number(assignedToUserID) : null,
                assignedToDepartmentID: assignedToDepartmentID ? Number(assignedToDepartmentID) : null,
                assignedToFunctionID: assignedToFunctionID ? Number(assignedToFunctionID) : null,
                userID: user.userID,
                dataJson: Object.keys(dataJson).length > 0 ? JSON.stringify(dataJson) : null,
                categoryId: itCategoryId ? Number(itCategoryId) : null,
                boardId: selectedBoardId && selectedBoardId !== -1 ? selectedBoardId : null
            };

            const res = await apiFetch('/api/ActionItems/action-items/manual', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();

            if (res.ok && data?.success) {
                // Add observers as collaborators
                if (observers.length > 0 && data.id) {
                    try {
                        await Promise.all(observers.map(observerId =>
                            apiFetch(`/api/ActionItems/action-items/${data.id}/collaborators`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    collaboratorUserId: Number(observerId),
                                    collaboratorRole: 'Observer',
                                    notes: 'Added as observer during action item creation',
                                    receiveNotifications: true,
                                    canEdit: false,
                                    addedByUserId: user.userID
                                })
                            })
                        ));
                    } catch (err) {
                        Sentry.captureException(err);
                        toast.warning('Action item created but some observers could not be added');
                    }
                }

                toast.success('Action item created');
                if (onCreated) onCreated();
                onClose();
            } else {
                toast.error(data?.message || 'Failed to save');
            }
        } catch (err) {
            Sentry.captureException(err);
            console.error(err);
            toast.error('Error while saving');
        } finally {
            setSaving(false);
        }
    };

    // ── Render helpers ──

    const renderTypeSelector = () => {
        if (loadingCreationTypes) {
            return (
                <div className="cai-loading">
                    <FaSpinner className="cai-spinner" />
                    <span>Loading available types...</span>
                </div>
            );
        }

        // Group types by module
        const grouped = {};
        const ungrouped = [];
        boardCreationTypes.forEach(type => {
            const mod = type.moduleDisplayName || type.moduleKey;
            if (mod) {
                if (!grouped[mod]) grouped[mod] = [];
                grouped[mod].push(type);
            } else {
                ungrouped.push(type);
            }
        });
        const moduleKeys = Object.keys(grouped);

        return (
            <select
                value={selectedCreationType?.assignmentId ?? ''}
                onChange={(e) => handleCreationTypeChange(parseInt(e.target.value))}
                className="cai-select"
            >
                {boardCreationTypes.length === 0 ? (
                    <option value="">No types configured</option>
                ) : moduleKeys.length <= 1 && ungrouped.length === 0 ? (
                    <>
                        {!selectedCreationType && <option value="">-- Select Type --</option>}
                        {boardCreationTypes.map(type => (
                            <option key={type.assignmentId} value={type.assignmentId}>
                                {type.displayName}
                            </option>
                        ))}
                    </>
                ) : (
                    <>
                        {!selectedCreationType && <option value="">-- Select Type --</option>}
                        {moduleKeys.map(mod => (
                            <optgroup key={mod} label={mod}>
                                {grouped[mod].map(type => (
                                    <option key={type.assignmentId} value={type.assignmentId}>
                                        {type.displayName}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                        {ungrouped.map(type => (
                            <option key={type.assignmentId} value={type.assignmentId}>
                                {type.displayName}
                            </option>
                        ))}
                    </>
                )}
            </select>
        );
    };

    const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];

    return (
        <div className="cai-backdrop" onClick={onClose}>
            <div className="cai-modal" onClick={(e) => e.stopPropagation()}>
                {/* ── Fixed Header ── */}
                <div className="cai-header">
                    <h2>Create Action Item</h2>
                    <button className="cai-close-btn" onClick={onClose} title="Close">
                        <FaTimes />
                    </button>
                </div>

                {/* ── Scrollable Body ── */}
                <div className="cai-body">
                    {/* Section 1: Basics */}
                    <div className="cai-section">
                        <div className="cai-basics-grid">
                            {/* Action Item Type */}
                            <div className="cai-field cai-field-full">
                                <label className="cai-label">Action Item Type <span className="cai-required">*</span></label>
                                {renderTypeSelector()}
                                {selectedCreationType?.description && (
                                    <small className="cai-hint">{selectedCreationType.description}</small>
                                )}
                            </div>

                            {/* Property */}
                            <div className="cai-field">
                                <label className="cai-label">Property <span className="cai-required">*</span></label>
                                <select
                                    value={propertyID}
                                    onChange={(e) => setPropertyID(e.target.value)}
                                    className="cai-select"
                                >
                                    {properties.length === 0 ? (
                                        <option value="">No properties available</option>
                                    ) : (
                                        <>
                                            {properties.length > 1 && (
                                                <option value="">-- Select Property --</option>
                                            )}
                                            {properties.map(p => (
                                                <option key={p.propertyID || p.PropertyID} value={p.propertyID || p.PropertyID}>
                                                    {p.propertyName || p.PropertyName}
                                                </option>
                                            ))}
                                        </>
                                    )}
                                </select>
                            </div>

                            {/* Priority */}
                            <div className="cai-field">
                                <label className="cai-label">Priority <span className="cai-required">*</span></label>
                                <select
                                    value={priority}
                                    onChange={(e) => setPriority(e.target.value)}
                                    className="cai-select"
                                >
                                    {PRIORITY_OPTIONS.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Title - Full width */}
                            <div className="cai-field cai-field-full">
                                <label className="cai-label">Title <span className="cai-required">*</span></label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Enter a short title for this action item"
                                    className="cai-input"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Section 2: Template Fields */}
                    {loadingTemplate ? (
                        <div className="cai-section cai-loading">
                            <FaSpinner className="cai-spinner" />
                            <span>Loading template fields...</span>
                        </div>
                    ) : activeTemplate ? (
                        <div className="cai-section cai-template-section">
                            <TemplateRenderer
                                key={activeTemplate.templateKey || activeTemplate.templateId}
                                template={activeTemplate}
                                onSubmit={null}
                                onCancel={null}
                                readOnly={false}
                                onChange={handleTemplateDataChange}
                                externalPropertyId={propertyID}
                            />
                        </div>
                    ) : null}

                    {/* Section 3: Assignment & Routing (Collapsible) */}
                    <div className="cai-section">
                        <button
                            type="button"
                            className="cai-accordion-toggle"
                            onClick={() => setAssignmentExpanded(!assignmentExpanded)}
                        >
                            <span>Assignment &amp; Routing</span>
                            {assignmentExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </button>

                        {assignmentExpanded && (
                            <div className="cai-accordion-body">
                                <div className="cai-basics-grid">
                                    {/* Assignee */}
                                    <div className="cai-field">
                                        <label className="cai-label">Assignee</label>
                                        <select
                                            value={assignedToUserID}
                                            onChange={(e) => setAssignedToUserID(e.target.value)}
                                            className="cai-select"
                                        >
                                            <option value="">-- Select User --</option>
                                            {allUsers.map(u => {
                                                const displayName = getUserDisplayName(u) || `User ${u.id}`;
                                                return (
                                                    <option key={u.id} value={u.id}>{displayName}</option>
                                                );
                                            })}
                                        </select>
                                    </div>

                                    {/* Department */}
                                    <div className="cai-field">
                                        <label className="cai-label">Department</label>
                                        <select
                                            value={assignedToDepartmentID}
                                            onChange={(e) => setAssignedToDepartmentID(e.target.value)}
                                            className="cai-select"
                                        >
                                            <option value="">-- Select Department --</option>
                                            {departments.map(dept => (
                                                <option key={dept.departmentID} value={dept.departmentID}>
                                                    {dept.departmentName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Function */}
                                    <div className="cai-field">
                                        <label className="cai-label">Function</label>
                                        <select
                                            value={assignedToFunctionID}
                                            onChange={(e) => setAssignedToFunctionID(e.target.value)}
                                            className="cai-select"
                                        >
                                            <option value="">-- Select Function --</option>
                                            {functions.map(func => (
                                                <option key={func.functionID} value={func.functionID}>
                                                    {func.functionName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* IT Category — only when IT module */}
                                    {selectedCreationType?.moduleKey === 'IT' && itCategories.length > 0 && (
                                        <div className="cai-field">
                                            <label className="cai-label">IT Category</label>
                                            <select
                                                value={itCategoryId}
                                                onChange={(e) => setItCategoryId(e.target.value)}
                                                className="cai-select"
                                            >
                                                <option value="">-- Select Category --</option>
                                                {itCategories.map(cat => (
                                                    <option key={cat.categoryId} value={cat.categoryId}>{cat.fullPath || cat.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>

                                {/* Observers */}
                                <div className="cai-field cai-field-full" style={{ marginTop: '12px' }}>
                                    <label className="cai-label">Observers</label>
                                    <div className="cai-observer-picker" ref={observerRef}>
                                        {observers.length > 0 && (
                                            <div className="cai-observer-tags">
                                                {observers.map(observerId => {
                                                    const userObj = allUsers.find(u => u.id === Number(observerId));
                                                    const displayName = getUserDisplayName(userObj) || `User ${observerId}`;
                                                    return (
                                                        <span key={observerId} className="cai-observer-tag">
                                                            {displayName}
                                                            <button
                                                                type="button"
                                                                onClick={() => setObservers(prev => prev.filter(id => id !== observerId))}
                                                                className="cai-observer-remove"
                                                            >
                                                                <FaTimes />
                                                            </button>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <div className="cai-observer-search">
                                            <FaSearch className="cai-observer-search-icon" />
                                            <input
                                                type="text"
                                                value={observerQuery}
                                                onChange={(e) => { setObserverQuery(e.target.value); setShowObserverDropdown(true); }}
                                                onFocus={() => setShowObserverDropdown(true)}
                                                placeholder="Search users to add as observers..."
                                                className="cai-input"
                                            />
                                        </div>
                                        {showObserverDropdown && (
                                            <div className="cai-observer-dropdown">
                                                {allUsers
                                                    .filter(u => {
                                                        if (observers.includes(String(u.id))) return false;
                                                        if (!observerQuery.trim()) return true;
                                                        const q = observerQuery.toLowerCase();
                                                        const displayName = getUserDisplayName(u) || '';
                                                        return displayName.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                                                    })
                                                    .slice(0, 10)
                                                    .map(u => {
                                                        const displayName = getUserDisplayName(u) || `User ${u.id}`;
                                                        return (
                                                            <div
                                                                key={u.id}
                                                                className="cai-observer-item"
                                                                onClick={() => {
                                                                    setObservers(prev => [...prev, String(u.id)]);
                                                                    setObserverQuery('');
                                                                    setShowObserverDropdown(false);
                                                                }}
                                                            >
                                                                <span className="cai-observer-name">{displayName}</span>
                                                                {u.email && <span className="cai-observer-email">{u.email}</span>}
                                                            </div>
                                                        );
                                                    })}
                                                {allUsers.filter(u => {
                                                    if (observers.includes(String(u.id))) return false;
                                                    if (!observerQuery.trim()) return true;
                                                    const q = observerQuery.toLowerCase();
                                                    const displayName = getUserDisplayName(u) || '';
                                                    return displayName.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                                                }).length === 0 && (
                                                    <div className="cai-observer-empty">
                                                        {observerQuery.trim() ? 'No users found' : 'All users added'}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Requester (read-only) */}
                                <div className="cai-field cai-field-full" style={{ marginTop: '12px' }}>
                                    <label className="cai-label">Requester</label>
                                    <input
                                        type="text"
                                        value={`${user.firstName || ''} ${user.lastName || ''}`.trim()}
                                        disabled
                                        className="cai-input cai-input-readonly"
                                    />
                                </div>

                                {/* Comments */}
                                <div className="cai-field cai-field-full" style={{ marginTop: '12px' }}>
                                    <label className="cai-label">Comments</label>
                                    <textarea
                                        value={comments}
                                        onChange={(e) => setComments(e.target.value)}
                                        placeholder="Add any additional comments..."
                                        rows={2}
                                        className="cai-input"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Fixed Footer ── */}
                <div className="cai-footer">
                    <button type="button" className="cai-btn cai-btn-cancel" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="cai-btn cai-btn-save"
                        onClick={handleSave}
                        disabled={saving || loadingTemplate || loadingCreationTypes}
                    >
                        {saving ? (
                            <>
                                <FaSpinner className="cai-spinner" />
                                Saving...
                            </>
                        ) : (
                            'Create Action Item'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateActionItemModal;
